import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { Box, Button, Table, TableBody } from "@mui/material"
import { TreeToolbar } from "slot-definitions"
import { useDialog } from "lib/@hooks/useDialog"
import Iconify from "minimals-template/components/Iconify"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { AuthoringUserRow } from "authoring/users/authoring-user-row"
import { Bound } from "lib/@components/binding/Bound"
import { RequestPageRepeat } from "lib/@components/request-page-repeat"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { useReferenceState } from "lib/@hooks/use-reference"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useState } from "react"
import { Frag } from "lib/@components/slot/frag"
import { useProject } from "authoring/lib/hooks/use-project"
import { useParams } from "react-router"
import { getProjectUsers } from "authoring/users/projects/controller/get-project-users"
import { AddContactDialog } from "authoring/routes/project/components/user-access/contact-dialog"
import { addUserToProject } from "authoring/users/controller/add-user-to-project"
import { directRemoveUserFromProject } from "authoring/users/controller/direct-remove-user-from-project"
import { confirmByUserInput } from "lib/@dialogs/confirm-by-user-input"
import { Secure } from "lib/authorization/secure"
import { PermissionDeniedUI } from "minimals-template/components/guards/PermissionDeniedUI"
import { AUTHORING_MANAGE_WORK_PACKAGE_USERS_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"

const PROJECT_USER_ACCESS_DEMANDS = [AUTHORING_MANAGE_WORK_PACKAGE_USERS_DEMAND]

export function ProjectUserList({ projectId }) {
    const [take, setTake] = useReferenceState("project-user-list", 10)
    const [search] = useSearchParam("search")
    const [skip, setSkip] = useState(0)

    const addClient = useDialog(
        <AddContactDialog projectId={projectId} />,
        {
            maxWidth: "md",
        },
        [projectId]
    )

    const projectUsers = getProjectUsers.useResults({ skip, take, searchTerm: search, projectId })
    const { total, users } = projectUsers ?? {}
    if (!projectUsers) return null
    return (
        <Bound projectUsers={projectUsers}>
            <TreeToolbar.Plug>
                <Box>
                    <Button
                        color="primary"
                        onClick={handleAddUser}
                        startIcon={<Iconify icon="mdi:user-add" />}
                        variant="contained"
                        data-cy="client-admin-add-user-button"
                    >
                        Add User
                    </Button>
                </Box>
            </TreeToolbar.Plug>
            <Box mt={1}>
                <SearchBoxUsingSearchParam sx={{ flex: 1, mr: 3, ml: 3 }} placeholder="Search for users" />

                <Table size="small">
                    <TableBody>
                        <RequestPageRepeat
                            pageSize={take}
                            setSkip={setSkip}
                            total={total}
                            list={users}
                            setTake={setTake}
                            item={<AuthoringUserRow roleSelector={false} onRemove={handleRemove} />}
                            ContentWrapper={Frag}
                        />
                    </TableBody>
                </Table>
            </Box>
        </Bound>
    )

    async function handleAddUser() {
        const newUser = await addClient()
        if (newUser) {
            try {
                await addUserToProject({ projectId, userId: newUser?.id })
                successSnackbar("User created")
                // TODO: invite here
            } catch (error) {
                console.error(error)
                errorSnackbar(error.toString())
            }
        }
    }

    async function handleRemove(projectUser) {
        if (
            await confirmByUserInput({
                message: "Are you sure you want to remove this user?",
                confirmValue: projectUser.UserName,
            })
        ) {
            await directRemoveUserFromProject({ projectUserId: projectUser?.id })
        }
    }
}

export function ProjectUsers() {
    const { projectId } = useParams("projectId")
    const { project } = useProject({ id: projectId })
    if (!project) return null

    return (
        <PageBreadcrumbContainer
            title={`User Access for ${project?.name}`}
            parentCrumbs={[
                { label: "Work Packages", to: "/authoring/projects" },
                { label: `${project.name}`, to: `/authoring/projects/${projectId}` },
            ]}
        >
            <Box flex={1}>
                <Secure
                    demand={PROJECT_USER_ACCESS_DEMANDS}
                    and={false}
                    fallback={
                        <Box
                            sx={{
                                minHeight: "60vh",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mt: 2,
                            }}
                        >
                            <PermissionDeniedUI
                                warning="Permission Denied"
                                message="You do not have permission to access this work package user access page"
                            />
                        </Box>
                    }
                >
                    <ProjectUserList projectId={projectId} />
                </Secure>
            </Box>
        </PageBreadcrumbContainer>
    )
}
